import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  WorkshopCostItem,
  OrderWorkshopCost,
  CalculationType,
  User,
} from "../types";
import { costService } from "../services/dataService";
import { formatNumber } from "../utils/formatters";
import AlertModal from "./AlertModal";
import ConfirmModal from "./ConfirmModal";
import "../pages/PurchaseOrders.css";
import "./WorkshopTransferModal.css";

/**
 * Atölye transfer modalı (po-* dili; ek sınıflar wt-*).
 *
 * Sipariş bir atölyeden diğerine geçerken, ESKİ atölyede kullanılan maliyet
 * kalemleri burada girilir; ardından transfer yapılır. Maliyet girişi isteğe
 * bağlıdır — kalem seçilmeden de transfer edilebilir.
 *
 * Çoğunlukla QR ile mobilde açıldığı için dar ekranda alttan açılan sayfa (sheet),
 * masaüstünde ortalı po-modal olarak çizilir. Kalem giriş formu ayrı bir
 * pencere DEĞİL, aynı modalın içinde ikinci bir görünümdür: mobilde üst üste
 * üç pencere açmak kullanılmaz hâle geliyordu.
 *
 * Esc sırası: onay/uyarı katmanı → kalem formu (listeye döner) → modal.
 */

type CostPayload = Omit<
  OrderWorkshopCost,
  | "orderWorkshopCostId"
  | "createdAt"
  | "createdBy"
  | "updatedAt"
  | "updatedBy"
  | "order"
  | "workshop"
  | "costItem"
>;

interface WorkshopTransferModalProps {
  isOpen: boolean;
  orderId: string;
  orderQuantity?: number;
  oldWorkshopId: string | null;
  oldWorkshopName: string;
  newWorkshopId: string | null;
  newWorkshopName: string;
  currentUser?: User;
  onClose: () => void;
  onSave: (costs: CostPayload[]) => Promise<void>;
}

/** Tek bir kalem için girilen değerler. */
interface CostInput {
  costItemId: string;
  calculationType: CalculationType;
  quantityUsed: number | "";
  quantity2?: number | "";
  quantity3?: number;
  totalCost?: number | "";
  unit: string;
  unit2?: string;
  unit3?: string;
  costUnitId3?: string;
  quantity1Label?: string;
  quantity2Label?: string;
  actualPrice: number;
  currency: string;
  notes: string;
}

// Hesaplama tipine göre kaç miktar alanı sorulur (sipariş adedi hariç)
const paramCount = (t: CalculationType): number => {
  switch (t) {
    case CalculationType.CustomCost:
      return 0;
    case CalculationType.Simple:
    case CalculationType.MeterBased:
      return 1;
    default:
      return 2;
  }
};

// Sipariş adedini kendiliğinden hesaba katan tipler
const usesOrderQty = (t: CalculationType) =>
  t === CalculationType.PieceFitting ||
  t === CalculationType.MeterBased ||
  t === CalculationType.AreaBased;

const num = (v: number | "" | undefined) => (v === "" || v === undefined ? 0 : v);

/** Kaydedilecek toplam — eski modaldaki formülün aynısı. */
const lineTotal = (i: CostInput): number => {
  if (i.calculationType === CalculationType.CustomCost) return num(i.totalCost);
  // Boya hesaplamada ikinci miktar (çeşit sayısı) bilgi amaçlıdır, çarpana girmez
  const second = i.calculationType === CalculationType.PaintBased ? 1 : num(i.quantity2) || 1;
  return num(i.quantityUsed) * second * i.actualPrice;
};

/** Alan bazında hata metinleri (boşsa geçerli). */
const fieldErrors = (i: CostInput): { total?: string; q1?: string; q2?: string } => {
  if (i.calculationType === CalculationType.CustomCost)
    return num(i.totalCost) > 0 ? {} : { total: "Toplam tutar sıfırdan büyük olmalı." };
  const e: { q1?: string; q2?: string } = {};
  if (num(i.quantityUsed) <= 0) e.q1 = "Miktar sıfırdan büyük olmalı.";
  if (paramCount(i.calculationType) >= 2 && num(i.quantity2) <= 0)
    e.q2 = `${i.quantity2Label || i.unit2 || "İkinci miktar"} girilmeli.`;
  return e;
};

const isValid = (i: CostInput): boolean => Object.keys(fieldErrors(i)).length === 0;

const I = {
  close: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  ),
  back: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m15 18-6-6 6-6" />
    </svg>
  ),
  arrow: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  ),
  search: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </svg>
  ),
  check: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="m5 12 5 5L20 7" />
    </svg>
  ),
  plus: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M12 5v14M5 12h14" />
    </svg>
  ),
  star: (
    <svg viewBox="0 0 24 24" fill="currentColor">
      <path d="m12 2 3 6.6 7 .8-5.2 4.8 1.4 7L12 17.8 5.8 21.2l1.4-7L2 9.4l7-.8z" />
    </svg>
  ),
};

const WorkshopTransferModal: React.FC<WorkshopTransferModalProps> = ({
  isOpen,
  orderId,
  orderQuantity,
  oldWorkshopId,
  oldWorkshopName,
  newWorkshopName,
  currentUser,
  onClose,
  onSave,
}) => {
  const [items, setItems] = useState<WorkshopCostItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState<string>("all");
  const [done, setDone] = useState<Map<string, CostInput>>(new Map());
  const [editing, setEditing] = useState<{ item: WorkshopCostItem; input: CostInput } | null>(null);
  const [tried, setTried] = useState(false); // kalem formunda "Kalemi ekle" denendi mi (hata metinleri için)

  const [alert, setAlert] = useState<{ isOpen: boolean; message: string; type: "warning" | "error" }>({
    isOpen: false,
    message: "",
    type: "warning",
  });
  const [confirm, setConfirm] = useState<{ isOpen: boolean; message: string }>({ isOpen: false, message: "" });

  const dialogRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // İlk atama: eski atölye yok → maliyet sorulmaz, yalnız onay alınır
  const hasOldWorkshop =
    !!oldWorkshopId && oldWorkshopId !== "null" && oldWorkshopId !== "undefined";
  const isFirstAssignment = !hasOldWorkshop || oldWorkshopName === "Atanmamış İşler";

  useEffect(() => {
    if (!isOpen) return;
    setDone(new Map());
    setEditing(null);
    setSearch("");
    setCategory("all");
    if (!hasOldWorkshop) {
      setItems([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const list = await costService.getWorkshopCostItems(oldWorkshopId!);
        if (cancelled) return;
        setItems(
          list
            .filter((x) => (x.isActive === undefined || x.isActive) && !!x.costItemId)
            .sort((a, b) => (a.priority || 999) - (b.priority || 999)),
        );
      } catch {
        if (!cancelled) setItems([]); // kalem yüklenemezse transfer yine yapılabilir
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, oldWorkshopId]);

  // Açılışta odak: fareli cihazda arama kutusu; dokunmatikte klavye açılmasın diye pencerenin kendisi.
  // Kapanınca odak açan öğeye döner.
  useEffect(() => {
    if (!isOpen) return;
    const opener = document.activeElement as HTMLElement | null;
    return () => {
      if (opener && opener.isConnected) opener.focus?.({ preventScroll: true });
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || loading || editing) return;
    const fine = typeof window.matchMedia === "function" && window.matchMedia("(pointer: fine)").matches;
    if (fine && searchRef.current) searchRef.current.focus({ preventScroll: true });
    else dialogRef.current?.focus({ preventScroll: true });
  }, [isOpen, loading, editing]);

  // Esc: önce iç katman (onay → uyarı → kalem formu), sonra modal. Yakalama aşamasında, dış dinleyicilere gitmez.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      if (confirm.isOpen) setConfirm({ isOpen: false, message: "" });
      else if (alert.isOpen) setAlert((a) => ({ ...a, isOpen: false }));
      else if (editing) closeItem();
      else if (!saving) onClose();
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, confirm.isOpen, alert.isOpen, editing, saving, onClose]);

  const categories = useMemo(
    () => Array.from(new Set(items.map((x) => x.categoryName).filter(Boolean) as string[])).sort(),
    [items],
  );

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter(
      (x) =>
        !done.has(x.costItemId) &&
        (!q || (x.costItemName || "").toLowerCase().includes(q)) &&
        (category === "all" || x.categoryName === category),
    );
  }, [items, done, search, category]);

  // Para birimine göre toplam — farklı birimler tek sayıya toplanmaz
  const totals = useMemo(() => {
    const m = new Map<string, number>();
    done.forEach((i) => m.set(i.currency, (m.get(i.currency) || 0) + lineTotal(i)));
    return Array.from(m.entries());
  }, [done]);

  // ---- Kalem giriş formu ----

  const openItem = (item: WorkshopCostItem, existing?: CostInput) => {
    setTried(false);
    if (existing) return setEditing({ item, input: existing });
    const t = item.calculationType ?? item.costItem?.calculationType ?? CalculationType.Simple;
    const n = paramCount(t);
    const withQty = usesOrderQty(t);
    setEditing({
      item,
      input: {
        costItemId: item.costItemId,
        calculationType: t,
        quantityUsed: "",
        quantity2: n >= 2 ? "" : undefined,
        quantity3: withQty ? orderQuantity || 0 : undefined,
        totalCost: t === CalculationType.CustomCost ? "" : undefined,
        unit: item.unitName || item.unitCode || "Birim",
        unit2: n >= 2 ? item.unitName2 || "Birim 2" : undefined,
        unit3: withQty ? "Sipariş Adedi" : undefined,
        costUnitId3: item.costUnitId3,
        quantity1Label: item.quantity1Label || item.costItem?.quantity1Label,
        quantity2Label: item.quantity2Label || item.costItem?.quantity2Label,
        actualPrice: item.effectivePrice,
        currency: item.effectiveCurrency || item.standardCurrency || "TRY",
        notes: "",
      },
    });
  };

  const closeItem = () => {
    setEditing(null);
    setTried(false);
  };

  const patch = (p: Partial<CostInput>) =>
    setEditing((e) => (e ? { ...e, input: { ...e.input, ...p } } : e));

  const commitItem = () => {
    if (!editing) return;
    const i = editing.input;
    if (!isValid(i)) {
      setTried(true); // hata metinleri alanların altında görünür
      return;
    }
    setDone((d) => new Map(d).set(i.costItemId, i));
    closeItem();
  };

  const removeItem = (id: string) =>
    setDone((d) => {
      const n = new Map(d);
      n.delete(id);
      return n;
    });

  // ---- Kaydet ----

  const askConfirm = () => {
    if (editing) {
      setAlert({ isOpen: true, type: "warning", message: "Açık kalem girişini kaydedin ya da vazgeçin." });
      return;
    }
    const n = done.size;
    setConfirm({
      isOpen: true,
      message: isFirstAssignment
        ? `${newWorkshopName} atölyesine atama yapılacak.\n\nOnaylıyor musunuz?`
        : `${oldWorkshopName} → ${newWorkshopName}${n > 0 ? `\n\n${n} maliyet kalemi kaydedilecek.` : "\n\nMaliyet kalemi girilmedi."}\n\nOnaylıyor musunuz?`,
    });
  };

  const performSave = async () => {
    setConfirm({ isOpen: false, message: "" });
    try {
      setSaving(true);
      // İlk atamada maliyet olmaz; eski modalla aynı kural
      const costs: CostPayload[] = isFirstAssignment
        ? []
        : Array.from(done.values()).map((i) => {
            const custom = i.calculationType === CalculationType.CustomCost;
            return {
              orderId,
              workshopId: oldWorkshopId || "",
              costItemId: i.costItemId,
              quantityUsed: custom ? 0 : num(i.quantityUsed),
              quantity2: custom ? undefined : num(i.quantity2) || undefined,
              quantity3: custom ? undefined : i.quantity3,
              unit: i.unit || "Birim",
              unit2: custom ? undefined : i.unit2,
              unit3: custom ? undefined : i.unit3,
              costUnitId3: custom ? undefined : i.costUnitId3,
              actualPrice: custom ? 0 : i.actualPrice,
              currency: i.currency,
              totalCost: lineTotal(i),
              notes: i.notes.trim() || undefined,
              isActive: true,
            };
          });
      await onSave(costs);
      setDone(new Map());
      setEditing(null);
    } catch (e: any) {
      setAlert({ isOpen: true, type: "error", message: e?.message || "İşlem başarısız oldu." });
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const primaryLabel = saving
    ? "Kaydediliyor…"
    : isFirstAssignment
      ? "Atamayı yap"
      : done.size > 0
        ? `${done.size} kalemle transfer et`
        : currentUser?.role === "Modelist"
          ? "Transfer et"
          : "Maliyetsiz transfer et";

  const doneRows = Array.from(done.values())
    .map((i) => ({ i, it: items.find((x) => x.costItemId === i.costItemId) }))
    .filter((r): r is { i: CostInput; it: WorkshopCostItem } => !!r.it);

  return (
    <div
      className="po-overlay center top wt-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !saving) onClose();
      }}
    >
      <div
        className="po-modal wt-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="wt-title"
        tabIndex={-1}
        ref={dialogRef}
      >
        <span className="wt-grip" aria-hidden="true" />

        {/* ===== Başlık ===== */}
        <div className="po-modal-head">
          <div className="wt-head-main">
            {editing && (
              <button type="button" className="po-ib big" onClick={closeItem} aria-label="Listeye dön">
                {I.back}
              </button>
            )}
            <div>
              <span className="po-kicker">
                {editing ? editing.item.categoryName || "Maliyet kalemi" : "Atölye transferi"}
              </span>
              <h3 id="wt-title">
                {editing
                  ? editing.item.costItemName
                  : isFirstAssignment
                    ? "Atölyeye ata"
                    : "Maliyet girin, transfer edin"}
              </h3>
            </div>
          </div>
          <button type="button" className="po-ib big" onClick={onClose} disabled={saving} aria-label="Kapat">
            {I.close}
          </button>
        </div>

        {/* ===== Gövde ===== */}
        <div className="po-modal-body wt-body">
          {!editing && (
            <div className="wt-route" aria-label={`${oldWorkshopName} atölyesinden ${newWorkshopName} atölyesine`}>
              <div className="wt-route-box">
                <span className="l">Şu an</span>
                <span className="v">{oldWorkshopName}</span>
              </div>
              <span className="wt-route-arrow" aria-hidden="true">
                {I.arrow}
              </span>
              <div className="wt-route-box next">
                <span className="l">Hedef</span>
                <span className="v">{newWorkshopName}</span>
              </div>
            </div>
          )}

          {editing ? (
            <ItemForm item={editing.item} input={editing.input} showErrors={tried} onChange={patch} />
          ) : isFirstAssignment ? (
            <div className="po-note info">
              <b>İlk atama.</b> Sipariş henüz bir atölyede çalışılmadı; maliyet girişi gerekmiyor.
              Onayladığınızda doğrudan <b>{newWorkshopName}</b> atölyesine atanır.
            </div>
          ) : loading ? (
            <div className="po-loading">
              <span className="po-spin" />
              Maliyet kalemleri yükleniyor…
            </div>
          ) : (
            <>
              {/* Girilen kalemler */}
              {doneRows.length > 0 && (
                <section className="po-sec">
                  <h3>
                    Girilen maliyetler <em>{doneRows.length}</em>
                  </h3>
                  <div className="po-table-wrap tight">
                    <table className="po-table small">
                      <thead>
                        <tr>
                          <th>Kalem</th>
                          <th className="num">Tutar</th>
                          <th className="act" aria-label="İşlem" />
                        </tr>
                      </thead>
                      <tbody>
                        {doneRows.map(({ i, it }) => (
                          <tr key={i.costItemId} onClick={() => openItem(it, i)} title="Düzenlemek için dokunun">
                            <td>
                              <div className="wt-line-name">{it.costItemName}</div>
                              <div className="po-sub">
                                {i.calculationType === CalculationType.CustomCost
                                  ? "Sabit tutar"
                                  : `${formatNumber(num(i.quantityUsed))} ${i.unit}${
                                      i.unit2 ? ` × ${formatNumber(num(i.quantity2))} ${i.unit2}` : ""
                                    }`}
                                {i.notes ? ` · ${i.notes}` : ""}
                              </div>
                            </td>
                            <td className="num wt-amt">
                              {formatNumber(lineTotal(i))} {i.currency}
                            </td>
                            <td className="act">
                              <button
                                type="button"
                                className="po-ib danger"
                                aria-label={`${it.costItemName} kalemini kaldır`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeItem(i.costItemId);
                                }}
                              >
                                {I.close}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        {totals.map(([c, v]) => (
                          <tr key={c} className="static wt-total">
                            <td>Toplam{totals.length > 1 ? ` (${c})` : ""}</td>
                            <td className="num">
                              {formatNumber(v)} {c}
                            </td>
                            <td />
                          </tr>
                        ))}
                      </tfoot>
                    </table>
                  </div>
                </section>
              )}

              {/* Kalem seçimi */}
              <section className="po-sec">
                <h3>
                  {oldWorkshopName} kalemleri
                  {done.size === 0 && <em>isteğe bağlı</em>}
                </h3>

                {items.length === 0 ? (
                  <div className="po-blank small">
                    <b>Tanımlı maliyet kalemi yok</b>
                    Bu atölye için maliyet kalemi tanımlanmamış. Maliyet girmeden devam edebilirsiniz.
                  </div>
                ) : (
                  <>
                    <label className="po-search wt-search">
                      {I.search}
                      <input
                        ref={searchRef}
                        type="search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Kalem ara…"
                        inputMode="search"
                        aria-label="Kalem ara"
                      />
                    </label>

                    {categories.length > 1 && (
                      <div className="wt-chips">
                        <div className="po-chipgroup" role="group" aria-label="Kategori">
                          <button
                            type="button"
                            className={category === "all" ? "on" : ""}
                            aria-pressed={category === "all"}
                            onClick={() => setCategory("all")}
                          >
                            Tümü
                          </button>
                          {categories.map((c) => (
                            <button
                              key={c}
                              type="button"
                              className={category === c ? "on" : ""}
                              aria-pressed={category === c}
                              onClick={() => setCategory(c)}
                            >
                              {c}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {visible.length === 0 ? (
                      <div className="po-blank small">
                        {done.size > 0 && !search && category === "all"
                          ? "Tüm kalemler girildi."
                          : "Eşleşen kalem yok."}
                      </div>
                    ) : (
                      <ul className="wt-list">
                        {visible.map((it) => (
                          <li key={it.workshopCostItemId}>
                            <button type="button" className="wt-item" onClick={() => openItem(it)}>
                              <span className="wt-item-main">
                                <span className="t">
                                  {it.costItemName}
                                  {it.isPreferred && (
                                    <span className="wt-star" title="Tercih edilen" aria-label="Tercih edilen">
                                      {I.star}
                                    </span>
                                  )}
                                </span>
                                <span className="po-sub">
                                  {it.categoryName || "Kategori yok"}
                                  {it.effectivePrice > 0 &&
                                    ` · ${formatNumber(it.effectivePrice)} ${
                                      it.effectiveCurrency || it.standardCurrency || "TRY"
                                    } / ${it.unitName || it.unitCode || "birim"}`}
                                </span>
                              </span>
                              <span className="wt-item-add" aria-hidden="true">
                                {I.plus}
                              </span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                )}
              </section>
            </>
          )}
        </div>

        {/* ===== Alt çubuk ===== */}
        <div className="po-modal-foot between wt-foot">
          {editing ? (
            <>
              <span className="po-hint wt-foot-hint">Kalem listeye eklenir; transfer onaylanınca kaydedilir.</span>
              <div className="po-foot-btns">
                <button type="button" className="po-btn" onClick={closeItem}>
                  Vazgeç
                </button>
                <button type="button" className="po-btn primary" onClick={commitItem} data-wt-commit>
                  {I.check}
                  Kalemi ekle
                </button>
              </div>
            </>
          ) : (
            <>
              {totals.length > 0 ? (
                <div className="po-sum">
                  <div className="grand">
                    <span>Toplam maliyet</span>
                    <b>{totals.map(([c, v]) => `${formatNumber(v)} ${c}`).join(" · ")}</b>
                  </div>
                </div>
              ) : (
                <span className="po-hint wt-foot-hint">
                  {isFirstAssignment ? "Maliyet girişi gerekmiyor." : "Maliyet girmeden de transfer edebilirsiniz."}
                </span>
              )}
              <div className="po-foot-btns">
                <button type="button" className="po-btn" onClick={onClose} disabled={saving}>
                  Vazgeç
                </button>
                <button type="button" className="po-btn primary" onClick={askConfirm} disabled={saving}>
                  {!saving && I.arrow}
                  {primaryLabel}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <AlertModal
        isOpen={alert.isOpen}
        message={alert.message}
        type={alert.type}
        onClose={() => setAlert((a) => ({ ...a, isOpen: false }))}
      />
      <ConfirmModal
        isOpen={confirm.isOpen}
        message={confirm.message}
        type="info"
        confirmText="Onayla"
        cancelText="İptal"
        onConfirm={performSave}
        onCancel={() => setConfirm({ isOpen: false, message: "" })}
      />
    </div>
  );
};

/* ---- Kalem giriş formu (modalın içinde, ayrı pencere değil) ---- */

const ItemForm: React.FC<{
  item: WorkshopCostItem;
  input: CostInput;
  showErrors: boolean;
  onChange: (p: Partial<CostInput>) => void;
}> = ({ input, showErrors, onChange }) => {
  const custom = input.calculationType === CalculationType.CustomCost;
  const paint = input.calculationType === CalculationType.PaintBased;
  const two = paramCount(input.calculationType) >= 2;
  const total = lineTotal(input);
  const err = showErrors ? fieldErrors(input) : {};

  const toNum = (v: string): number | "" => (v === "" ? "" : parseFloat(v));

  return (
    <div className="wt-form">
      {!custom && (
        <dl className="po-facts">
          <div>
            <dt>Birim fiyat</dt>
            <dd>
              {formatNumber(input.actualPrice)} {input.currency} / {input.unit}
            </dd>
          </div>
          <div>
            <dt>Hesap</dt>
            <dd>
              {two && !paint ? `${input.quantity1Label || "Miktar"} × ${input.quantity2Label || input.unit2}` : input.quantity1Label || "Miktar"}
              {input.unit3 ? " × sipariş adedi" : ""}
            </dd>
          </div>
        </dl>
      )}

      {custom ? (
        <>
          <div className="po-note info">
            <b>Sabit tutar.</b> Bu kalemde miktar ve birim fiyat yok; toplam tutar doğrudan girilir.
          </div>
          <label className="po-field">
            <span>
              Toplam tutar <em>{input.currency}</em>
            </span>
            <input
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              autoFocus
              className={err.total ? "is-invalid" : ""}
              aria-invalid={!!err.total}
              value={input.totalCost ?? ""}
              onChange={(e) => onChange({ totalCost: toNum(e.target.value) })}
              placeholder="0,00"
            />
            {err.total && <span className="po-fielderr">{err.total}</span>}
          </label>
        </>
      ) : (
        <>
          <div className={two ? "po-grid2 wt-grid2" : ""}>
            <label className="po-field">
              <span>
                {input.quantity1Label || "Miktar"} <em>{input.unit}</em>
              </span>
              <input
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0"
                autoFocus
                className={err.q1 ? "is-invalid" : ""}
                aria-invalid={!!err.q1}
                value={input.quantityUsed}
                onChange={(e) => onChange({ quantityUsed: toNum(e.target.value) })}
                placeholder="0"
              />
              {err.q1 && <span className="po-fielderr">{err.q1}</span>}
            </label>
            {two && (
              <label className="po-field">
                <span>
                  {input.quantity2Label || input.unit2} {!paint && <em>{input.unit2}</em>}
                </span>
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min="0"
                  className={err.q2 ? "is-invalid" : ""}
                  aria-invalid={!!err.q2}
                  value={input.quantity2 ?? ""}
                  onChange={(e) => onChange({ quantity2: toNum(e.target.value) })}
                  placeholder="0"
                />
                {err.q2 && <span className="po-fielderr">{err.q2}</span>}
              </label>
            )}
          </div>

          {paint && <p className="po-fieldnote">Çeşit sayısı bilgi amaçlıdır, hesaplamaya katılmaz.</p>}
          {input.unit3 && (
            <p className="po-fieldnote">Sipariş adedi ({input.quantity3}) hesaba kendiliğinden katılır.</p>
          )}
        </>
      )}

      <label className="po-field">
        <span>
          Not <em>isteğe bağlı</em>
        </span>
        <textarea
          rows={2}
          value={input.notes}
          onChange={(e) => onChange({ notes: e.target.value })}
          placeholder="Ör. fazla mesai, ek işlem"
        />
      </label>

      <div className="po-total-row grand wt-form-total">
        <span>Kalem toplamı</span>
        <b>
          {formatNumber(total)} {input.currency}
        </b>
      </div>
    </div>
  );
};

export default WorkshopTransferModal;
